import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Column } from "iuvity-ui-components/dist/column";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { FormSkeleton } from "../../components";
import { QuickLinkCreator } from "../../components/quicklinks/QuicklinkCreator";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useQuickLinks } from "../../hooks/useQuickLinks";
import { useVersions } from "../../hooks/useVersions";
import { QuickLinkObject, Version } from "../../interfaces";
import { AppContext } from "../../context/index";
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { Button } from "iuvity-ui-components/dist/button";

const filterDefaultValues: {
  name: string;
  description: string;
  labelkey: string;
  type: string;
  issecuredurl: string;
  iconname: string;
  linkto: string;
  isexternallink: string;
  status: string;
  releaseversion: string;
} = {
  name: '',
  description: '',
  labelkey: '',
  type: '',
  issecuredurl: '',
  iconname: '',
  linkto: '',
  isexternallink: '',
  status: '',
  releaseversion: '',
};

export const AdminQuicklinksPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { language, permissiondInfoByRole, pageFilter },
    setAppState,
  } = useContext(AppContext);

  const { data, quickLinkAction, reloadFn } = useQuickLinks("DATA");
  const { data: versionsData } = useVersions("DATA");
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
    ? {
      name: pageFilter?.name || '',
      description: pageFilter?.description || '',
      labelkey: pageFilter?.labelkey || '',
      type: pageFilter?.type || '',
      issecuredurl: pageFilter?.issecuredurl || '',
      iconname: pageFilter?.iconname || '',
      linkto: pageFilter?.linkto || '',
      isexternallink: pageFilter?.isexternallink || '',
      status: pageFilter?.status || '',
      releaseversion: pageFilter?.releaseversion || '',
    }
    : filterDefaultValues,
  });

  const { control, setValue, getValues } = form;
  const nameValues = useWatch({
    control,
    name: "name",
  });

  const descriptionValues = useWatch({
    control,
    name: "description",
  });

  const labelkeyValues = useWatch({
    control,
    name: "labelkey",
  });

  const typeValues = useWatch({
    control,
    name: "type",
  });

  const issecuredurlValues = useWatch({
    control,
    name: "issecuredurl",
  });

  const iconnameValues = useWatch({
    control,
    name: "iconname",
  });

  const linktoValues = useWatch({
    control,
    name: "linkto",
  });

  const isexternallinkValues = useWatch({
    control,
    name: "isexternallink",
  });

  const statusValues = useWatch({
    control,
    name: "status",
  });

  const releaseversionValues = useWatch({
    control,
    name: "releaseversion",
  });

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        header: t("Quicklinks.nameLabel"),
      },
      {
        field: "description",
        header: t("Quicklinks.descriptionLabel"),
      },
      {
        field: "labelkey",
        header: t("Quicklinks.labelkeyLabel"),
      },
      {
        field: "type",
        header: t("Quicklinks.typeLabel"),
      },
      {
        field: "issecuredurl",
        header: t("Quicklinks.issecuredurlLabel"),
      },
      {
        field: "iconname",
        header: t("Quicklinks.iconnameLabel"),
      },
      {
        field: "linkto",
        header: t("Quicklinks.linktoLabel"),
      },
      {
        field: "isexternallink",
        header: t("Quicklinks.isexternallinkLabel"),
      },
      {
        field: "status",
        header: t("Quicklinks.statusLabel"),
      },
      {
        field: "releaseversion",
        header: t("Quicklinks.releaseversionLabel"),
      },
      {
        field: "actions",
        header: t("Quicklinks.actionsLabel"),
      },
    ];
  }, [t]);

  const [visible, setVisible] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.IN },
    description: { value: null, matchMode: FilterMatchMode.IN },
    labelkey: { value: null, matchMode: FilterMatchMode.IN },
    type: { value: null, matchMode: FilterMatchMode.IN },
    issecuredurl: { value: null, matchMode: FilterMatchMode.IN },
    iconname: { value: null, matchMode: FilterMatchMode.IN },
    linkto: { value: null, matchMode: FilterMatchMode.IN },
    isexternallink: { value: null, matchMode: FilterMatchMode.IN },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    releaseversion: { value: null, matchMode: FilterMatchMode.IN },
  };
  const [filters, setFilters] = useState(defaultFilterValues);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const actionEditFn = (quicklink: QuickLinkObject) => {
    setAppState((prev) => ({
      ...prev,
      selectedQuickLink: quicklink,
    }));
    navigate(`${location.pathname}/edit`);
    window.scrollTo(0, 0);
  };

  const actionDeleteFn = async (quicklink: QuickLinkObject) => {
    await quickLinkAction("DELETE", quicklink.id);
    reloadFn();
  };

  const handleModalProps = async (quicklink: QuickLinkObject) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("Quicklinks.deleteModalTitle"),
      content: `${t("Quicklinks.deleteModalContent1")} "${quicklink?.name}" ${t(
        "Quicklinks.deleteModalContent2"
      )}`,
      onConfirm: () => actionDeleteFn(quicklink),
    }));
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Quicklinks.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const onClearFilters = () => {
    setFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(filterDefaultValues);
  };

  const [disableClearFilterButton, setDisableClearFilterButton] = useState<{
    name: boolean;
    alias: boolean;
    shortname: boolean;
    primarycolor: boolean;
    secondarycolor: boolean;
    logoname: boolean;
    isotypelogoname: boolean;
    status: boolean;
    guid: boolean;
    createdat: boolean;
    tenantcode: boolean;
    parent: boolean;
  }>({
    name: false,
    alias: false,
    shortname: false,
    primarycolor: false,
    secondarycolor: false,
    logoname: false,
    isotypelogoname: false,
    status: false,
    guid: false,
    createdat: false,
    tenantcode: false,
    parent: false
  });
  const onClearFiltersPerTable = (options: any, filtersFromSession: any) => {
    setFilters({
      global: { value: options?.field === 'global' ? null : filtersFromSession?.global?.value, matchMode: FilterMatchMode.CONTAINS },
      name: { value: options?.field === 'name' ? null : filtersFromSession?.name?.value, matchMode: FilterMatchMode.IN },
      description: { value: options?.field === 'description' ? null : filtersFromSession?.description?.value, matchMode: FilterMatchMode.IN },
      labelkey: { value: options?.field === 'labelkey' ? null : filtersFromSession?.labelkey?.value, matchMode: FilterMatchMode.IN },
      type: { value: options?.field === 'type' ? null : filtersFromSession?.type?.value, matchMode: FilterMatchMode.IN },
      issecuredurl: { value: options?.field === 'issecuredurl' ? null : filtersFromSession?.issecuredurl?.value, matchMode: FilterMatchMode.IN },
      iconname: { value: options?.field === 'iconname' ? null : filtersFromSession?.iconname?.value, matchMode: FilterMatchMode.IN },
      linkto: { value: options?.field === 'linkto' ? null : filtersFromSession?.linkto?.value, matchMode: FilterMatchMode.IN },
      isexternallink: { value: options?.field === 'isexternallink' ? null : filtersFromSession?.isexternallink?.value, matchMode: FilterMatchMode.IN },
      status: { value: options?.field === 'status' ? null : filtersFromSession?.status?.value, matchMode: FilterMatchMode.EQUALS },
      releaseversion: { value: options?.field === 'releaseversion' ? null : filtersFromSession?.releaseversion?.value, matchMode: FilterMatchMode.IN },
    });

    // luego de dar click en limpiar, elimina los criterios de búsqueda.
    setValue(options?.field, '');
    setDisableClearFilterButton((prev: any) => ({
      ...prev,
      [options?.field]: true,
    }));
    setAppState((prev) => ({
      ...prev,
      pageFilter: {
        ...prev.pageFilter,
        [options.field]: '',
      }
    }));
  };

  const statuses = [
    `${t("Tenants.activeStatusLabel")}`,
    `${t("Tenants.inactiveStatusLabel")}`,
  ];

  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };

  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
            options.filterCallback(e.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-3">
                {t(`Quicklinks.${options.field}Label`)}
              </label>
              {/* // @ts-ignore */}
              <Dropdown
                options={statuses}
                itemTemplate={statusItemTemplate}
                placeholder={t(`Quicklinks.${options.field}FilterPlaceholder`)}
                className="p-column-filter mb-2"
                showClear={false}
                {...field}
              />
            </>
          );
        }}
      />

    )
  };

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  }

  const filterTemplate = (options: any) => {
    setTimeout(() => {
      const emptyChipList = document.getElementsByClassName('p-multiselect-token-label');
      [...emptyChipList].map((chip) => {
        if (chip.innerHTML.toString() === '') {
          chip.parentElement!.style.display = 'none';
        };
      });
    }, 100);

    const arrayOriginal = data
      ?.map((quicklink: any) =>
        ['type'].includes(options.field)
          ? ({
            label: t(`Quicklinks.${quicklink?.type?.toLocaleLowerCase()}OptionLabel`),
            value: t(`Quicklinks.${quicklink?.type?.toLocaleLowerCase()}OptionLabel`),
          })
          : ['isexternallink','issecuredurl'].includes(options.field)
            ? ({
              label: t(`Quicklinks.${`${quicklink[options.field]}`.toLocaleLowerCase()}Label`),
              value: t(`Quicklinks.${`${quicklink[options.field]}`.toLocaleLowerCase()}Label`),
            })
            : ({ label: quicklink[`${options.field}`], value: quicklink[`${options.field}`] }))
      ?.filter((item: { label: string, value: string }) => item.label)
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label));

    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      options.field === 'status'
        ? statusFilterTemplate(options)
        : <Controller
          name={options.field}
          control={control}
          // rules={TenantCreatorSchema(t).name}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`Quicklinks.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={arrayWithoutDuplicates}
                  optionLabel={'label'}
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`Quicklinks.${options.field}FilterPlaceholder`)}
                  className="mb-2"
                  emptyFilterMessage={t("Tenants.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
        />
    )
  };

  const filterClearTemplate = (options: any) => {
    const filtersFromSession = JSON.parse(sessionStorage?.getItem('filters-table') ?? '{}')?.filters;

    return (
      <Button
        label={t("Tenants.clearFilterLabel")}
        variant="p-button-text"
        disabled={(filtersFromSession?.hasOwnProperty('global')
          ? !filtersFromSession[options?.field]?.value : true)
          // @ts-ignore
          || disableClearFilterButton[options?.field]}
        onClick={() => onClearFiltersPerTable(options, filtersFromSession)}
      />
    )
  }

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        itemID={options?.field}
        label={t("Tenants.applyFilterLabel")}
        className="p-button"
        disabled={
          !(
            (options?.field === 'name' && nameValues?.length > 0) ||
            (options?.field === 'description' && descriptionValues?.length > 0) ||
            (options?.field === 'labelkey' && labelkeyValues?.length > 0) ||
            (options?.field === 'type' && typeValues?.length > 0) ||
            (options?.field === 'issecuredurl' && issecuredurlValues?.length > 0) ||
            (options?.field === 'iconname' && iconnameValues?.length > 0) ||
            (options?.field === 'linkto' && linktoValues?.length > 0) ||
            (options?.field === 'isexternallink' && isexternallinkValues?.length > 0) ||
            (options?.field === 'status' && statusValues?.length > 0) ||
            (options?.field === 'releaseversion' && releaseversionValues?.length > 0)
          )
        }
        onClick={() => {
          options.filterApplyCallback();
          setAppState((prev) => ({
            ...prev,
            pageFilter: { ...getValues() },
          }));
          setDisableClearFilterButton((prev: any) => ({
            ...prev,
            [options?.field]: false,
          }));
        }}
      />
    )
  };

  useEffect(() => {
    if (sessionStorage.getItem('filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <>
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("Quicklinks.quickLinksTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Quicklinks.quickLinksTitle"),
              style: { fontWeight: "bold" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("Quicklinks.quickLinksDescription1")}</span>
        </div>
      </TransactionalSubheader>

      <SubheaderActions
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("Quicklinks.newTitle"),
            style: {
              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
            },
            onClick: () => navigate(`${location.pathname}/new`),
          },
        ])}
      />

      <BoxFull className="dt-c my-4">
        {data ? (
          <DataTable
            header={
              <TableHeader
                title={t("Quicklinks.quickLinksTitle")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFilters}
                globalFilterValue={globalFilterValue}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            editMode="row"
            dataKey="id"
            sortField="functionality"
            sortOrder={1}
            filters={filters}
            filterDisplay="menu"
            value={data.map((quicklink: QuickLinkObject) => ({
              ...quicklink,
              isexternallink: t(
                `Quicklinks.${`${quicklink?.isexternallink}`.toLocaleLowerCase()}Label`
              ),
              issecuredurl: t(
                `Quicklinks.${`${quicklink?.issecuredurl}`.toLocaleLowerCase()}Label`
              ),
              type: t(
                `Quicklinks.${`${quicklink.type}`.toLocaleLowerCase()}OptionLabel`
              ),
              status: t(
                `Quicklinks.${`${quicklink.status}`.toLocaleLowerCase()}Label`
              ),
              actions: (
                <Actions
                  enableDelete={!!quicklink.createdby}
                  onEdit={() => actionEditFn(quicklink)}
                  onDelete={() => handleModalProps(quicklink)}
                  visibleObject={{...permissiondInfoByRole}}
                />
              ),
            }))}
            scrollable
            resizableColumns
            stateKey='filters-table'
          >
            {selectedColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={!(col.field === "actions")}
                align={col.field === "actions" ? "center" : "left"}
                headerStyle={col.field === "actions"
                  ? {
                      justifyContent: "center",
                      display: (!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined,
                    }
                  : undefined }
                style={{
                  boxShadow: (col.field === "actions")
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : undefined,
                  whiteSpace: "normal",
                  display: (col.field === "actions")
                    ? ((!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined)
                    : undefined,
                }}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'createdby')}
                filter={col.field !== 'actions'}
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
                filterElement={filterTemplate}
                showFilterMenu
                showApplyButton
                showClearButton
              />
            ))}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      {versionsData && (
        <QuickLinkCreator
          visible={visible}
          setVisible={setVisible}
          createFn={quickLinkAction}
          reloadFn={reloadFn}
          auxModalController={setModalProps}
          options={versionsData
            .map((ver: Version) => ({
              label: ver.name,
              value: ver.name,
            }))
            .sort((a: any, b: any) => b.label.localeCompare(a.label))}
        />
      )}

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
